import React, { useState } from 'react';
import { Card, CardHeader, CardContent } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Icon } from '@/shared/icons';
import { useChannelRateLimits } from '../../hooks';
import { RateLimitsModal } from './RateLimitsModal';

interface RateLimitsCardProps {
  channelId: string;
}

export const RateLimitsCard: React.FC<RateLimitsCardProps> = ({ channelId }) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const { data, isLoading, isError } = useChannelRateLimits(channelId, true);

  if (isError) {
    return null;  // canal sem integration → silenciosamente esconde o card
  }

  if (isLoading || !data) {
    return (
      <Card>
        <CardHeader className="pb-3 px-4 py-3">
          <div className="flex items-center gap-2">
            <Icon name="zap" className="w-5 h-5" style={{ color: 'var(--theme-button-primary)' }} />
            <h3 className="font-semibold text-lg" style={{ color: 'var(--theme-card-text)' }}>
              Limites de envio
            </h3>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <p className="text-sm" style={{ color: 'var(--theme-card-muted)' }}>Carregando...</p>
        </CardContent>
      </Card>
    );
  }

  const intervalSeconds = data.rateLimits.messagesPerHour > 0
    ? Math.round(3600 / data.rateLimits.messagesPerHour)
    : 0;
  const intervalLabel = intervalSeconds >= 60
    ? `${Math.round(intervalSeconds / 60)} min`
    : `${intervalSeconds} s`;
  const isManual = data.rateLimits.source === 'manual';

  return (
    <Card>
      <CardHeader className="pb-3 px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon name="zap" className="w-5 h-5" style={{ color: 'var(--theme-button-primary)' }} />
          <h3 className="font-semibold text-lg" style={{ color: 'var(--theme-card-text)' }}>
            Limites de envio
          </h3>
        </div>
      </CardHeader>

      <CardContent className="px-4 pb-4">
        <div
          className="flex items-center justify-between p-4 rounded-lg"
          style={{ backgroundColor: 'var(--theme-card-hover)' }}
          data-testid="rate-limits-card"
        >
          <div className="flex items-center gap-3">
            <Icon name="clock" className="w-5 h-5" style={{ color: 'var(--theme-button-primary)' }} />
            <div>
              <p className="font-medium" style={{ color: 'var(--theme-card-text)' }}>
                Intervalo: <strong>{intervalLabel}</strong>
                <span
                  className="ml-2 text-xs px-2 py-0.5 rounded"
                  style={{
                    backgroundColor: isManual ? '#fef3c7' : '#dcfce7',
                    color: isManual ? '#92400e' : '#166534',
                  }}
                  data-testid="rate-limits-source-badge"
                >
                  {isManual ? 'Manual' : 'Automático'}
                </span>
              </p>
              <p className="text-xs" style={{ color: 'var(--theme-card-muted)' }}>
                Limite diário: {data.rateLimits.messagesPerDay.toLocaleString('pt-BR')} mensagens •
                Hoje: {data.currentUsage.sentToday} de {data.currentUsage.dailyLimit}
              </p>
              <p className="text-xs" style={{ color: 'var(--theme-card-muted)' }} data-testid="rate-limits-card-conversation">
                Automações: 1 a cada {Math.max(1, Math.round((data.rateLimits.conversationMinDelayMs ?? 3000) / 1000))} s •
                até {(data.rateLimits.conversationMessagesPerHour ?? 300).toLocaleString('pt-BR')} por hora
              </p>
            </div>
          </div>

          <Button
            variant="secondary"
            size="sm"
            onClick={() => setModalOpen(true)}
            data-testid="rate-limits-card-edit-btn"
          >
            <Icon name="edit" className="w-4 h-4 mr-2" />
            Editar
          </Button>
        </div>

        <RateLimitsModal
          channelId={channelId}
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
          current={data}
        />
      </CardContent>
    </Card>
  );
};

RateLimitsCard.displayName = 'RateLimitsCard';
